import React, { useState } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  getFirestore,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "../../firebase/firebase";
import { useNavigate } from "react-router-dom";

const CreateStory = () => {
  const navigate = useNavigate();
  const db = getFirestore();
  const storage = getStorage();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [slides, setSlides] = useState([]);

  const uploadFile = async (file, path) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const addSlide = () => {
    setSlides([
      ...slides,
      {
        mediaType: "image",
        mediaFile: null,
        ctaText: "",
        ctaUrl: "",
        ctaImage: null,
        tags: [],
      },
    ]);
  };

  const updateSlide = (i, field, value) => {
    const copy = [...slides];
    copy[i][field] = value;
    setSlides(copy);
  };

  const addTag = (i, tag) => {
    if (!tag || slides[i].tags.length >= 3) return;
    updateSlide(i, "tags", [...slides[i].tags, tag]);
  };

  const removeTag = (i, tag) => {
    updateSlide(i, "tags", slides[i].tags.filter((t) => t !== tag));
  }

  const removeSlide = (index) => {
    setSlides(slides.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!coverFile || !slides.length) {
      alert("Cover image & slides required");
      return;
    }

    const coverImageUrl = await uploadFile(coverFile, `stories/${slug}/cover`);

    const uploadedSlides = [];
    for (let i = 0; i < slides.length; i++) {
      const s = slides[i];

      const mediaUrl = await uploadFile(
        s.mediaFile,
        `stories/${slug}/slide-${i + 1}`,
      );

      const ctaImageUrl = s.ctaImage
        ? await uploadFile(s.ctaImage, `stories/${slug}/cta-${i + 1}`)
        : "";

      uploadedSlides.push({
        order: i + 1,
        mediaType: s.mediaType,
        mediaUrl,
        ctaText: s.ctaText,
        ctaUrl: s.ctaUrl,
        ctaImageUrl,
        tags: s.tags,
      });
    }

    await addDoc(collection(db, "stories"), {
      title,
      slug,
      description,
      coverImageUrl,
      slides: uploadedSlides,
      published: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    navigate("/dashboard/stories");
  };

  return (
    <div className="page-container">
      <h1>Create Story</h1>

      <form onSubmit={handleSubmit}>
        <div className="card p-4 mb-4">
          <input
            className="form-control mb-2"
            placeholder="Story Title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
            }}
            required
          />

          <input
            className="form-control mb-3"
            placeholder="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
          />

          <textarea
            className="form-control mb-2"
            placeholder="Short Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={(e) => setCoverFile(e.target.files[0])}
            required
          />
        </div>

        <div className="card p-4">
          <h5>Slides</h5>

          {slides.map((s, i) => (
            <div key={i} className="border rounded p-3 mb-3">
              <strong>Slide {i + 1}</strong>

              <select
                className="form-control mt-2"
                value={s.mediaType}
                onChange={(e) => updateSlide(i, "mediaType", e.target.value)}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>

              <input
                type="file"
                className="form-control mt-2"
                accept={s.mediaType === "image" ? "image/*" : "video/*"}
                onChange={(e) => updateSlide(i, "mediaFile", e.target.files[0])}
                required
              />

              <input
                className="form-control mt-3"
                placeholder="CTA Text"
                value={s.ctaText}
                onChange={(e) => updateSlide(i, "ctaText", e.target.value)}
              />

              <input
                className="form-control mt-2"
                placeholder="CTA URL"
                value={s.ctaUrl}
                onChange={(e) => updateSlide(i, "ctaUrl", e.target.value)}
              />

              <input
                type="file"
                className="form-control mt-2"
                accept="image/*"
                onChange={(e) => updateSlide(i, "ctaImage", e.target.files[0])}
              />

              <div className="mt-2">
                <input
                  className="form-control"
                  placeholder="Add tag (max 3)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag(i, e.target.value.trim());
                      e.target.value = "";
                    }
                  }}
                />

                <div className="mt-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="badge bg-secondary me-2"
                      onClick={() => removeTag(i, t)}
                    >
                      {t} ✕
                    </span>
                  ))}
                </div>

                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm mt-3"
                  onClick={() => removeSlide(i)}
                >
                  Remove Slide
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={addSlide}
          >
            + Add Slide
          </button>
        </div>

        <button className="btn btn-primary mt-4">Publish Story</button>
      </form>
    </div>
  );
};

export default CreateStory;
